import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CartProvider, useCart } from './context/CartContext';
import { AppProvider, useAppContext } from './context/AppContext';
import { TbkConnectionProvider } from './context/TbkConnectionContext';
import { XSignConnectionProvider } from './context/XSignConnectionContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import TokenLogin from './components/auth/TokenLogin';
import MainLayout from './components/layout/MainLayout';
import CategorySelectionScreen from './components/categories/CategorySelectionScreen';
import ProductList from './components/products/ProductList';
import ProductListWithSidebar from './components/products/ProductListWithSidebar';
import CartScreen from './components/cart/CartScreen';
import PaymentScreen from './components/checkout/PaymentScreen';
import PaymentMethodScreen from './components/checkout/PaymentMethodScreen';
import OperateCardScreen from './components/checkout/OperateCardScreen';
import ThankYouScreen from './components/checkout/ThankYouScreen';
import OrderTypeScreen from './components/checkout/OrderTypeScreen';
import WelcomeScreen from './components/welcome/WelcomeScreen';
import useInactivityTimer from './hooks/useInactivityTimer';
import useKioskLockdown from './hooks/useKioskLockdown';
import KioskExitGate from './components/kiosk/KioskExitGate';
import connectionService from './services/ConnectionService';
import axios from 'axios';
import OfflineService from './services/OfflineService';
import './index.css';

const queryClient = new QueryClient();

const TimeoutWarningModal = ({ onContinue, onExit }) => {
  const [countdown, setCountdown] = useState(20);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (countdown <= 0) {
      onExit();
    }
  }, [countdown, onExit]);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.85)', zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ backgroundColor: '#ffffff', padding: '2.5rem', borderRadius: '1.5rem', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', textAlign: 'center', maxWidth: '32rem', width: '90%' }}>
        <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', color: '#111827', margin: '0 0 1rem 0' }}>¿Desea continuar?</h2>
        <p style={{ fontSize: '1.5rem', color: '#4B5563', margin: '0 0 2rem 0' }}>
          Su sesión expirará en <span style={{ fontWeight: 'bold', color: '#e1251b', fontSize: '2.5rem' }}>{countdown}</span> segundos
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem' }}>
          <button onClick={onContinue} style={{ padding: '1.25rem 2.5rem', backgroundColor: '#DA291C', color: 'white', fontWeight: 'bold', borderRadius: '1rem', fontSize: '1.5rem', border: 'none', cursor: 'pointer' }}>
            Continuar
          </button>
          <button onClick={onExit} style={{ padding: '1.25rem 2.5rem', backgroundColor: '#e1251b', color: 'white', fontWeight: 'bold', borderRadius: '1rem', fontSize: '1.5rem', border: 'none', cursor: 'pointer' }}>
            Salir
          </button>
        </div>
      </div>
    </div>
  );
};

// Definido FUERA de AppRoutes: si se define dentro, es una función nueva en cada
// render y React remonta toda la ruta al cambiar el carrito (se perdía la categoría
// activa y la notificación de "agregado"). Estable = no remonta.
const ProtectedRoute = ({ isAuthenticated, children }) => {
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AppRoutes = ({ isAuthenticated, setIsAuthenticated }) => {
  const { clearSessionData, sessionData, settings } = useAppContext();
  const { items, clearCart } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [cajaCerrada, setCajaCerrada] = useState(false);

  useKioskLockdown();

  // ── Vigilancia de la sesión de caja ──
  // Se pregunta cada 5 s si la sesión sigue abierta. Cada consulta programa la siguiente al terminar y un
  // vigilante independiente la relanza si pasaron más de 20 s sin consultar (por si el temporizador se pierde).
  // También se consulta al cambiar de pantalla (incluido justo antes de pagar y al salir de pago) y al volver a
  // mostrarse la página. En las pantallas de pago no se interrumpe un cobro, salvo que lleve más de 10 min ahí
  // (se da por trabado). Bloquea solo si Odoo responde cerrada, en cierre o inexistente; sin red sigue vendiendo.
  const sessionId = sessionData?.session_id;
  const apiUrl = settings?.apiUrl || 'http://localhost:9000';
  const enPago = ['/payment', '/payment-method', '/operate-card', '/thank-you'].includes(location.pathname);
  const enPagoRef = useRef(false);
  const enPagoDesdeRef = useRef(0);
  const ultimaConsultaRef = useRef(0);
  const consultandoRef = useRef(false);
  const itemsRef = useRef(0);
  const cargadoEnRef = useRef(Date.now());
  if (enPago && !enPagoRef.current) enPagoDesdeRef.current = Date.now();
  enPagoRef.current = enPago;
  itemsRef.current = items.length;

  const odooOkRef = useRef(0);   // última vez que Odoo respondió (para no recargar sin internet)
  const verificarSesion = useCallback(async () => {
    ultimaConsultaRef.current = Date.now();   // marca que el ciclo sigue vivo
    if (!sessionId || consultandoRef.current) return;
    const pagoTrabado = () => enPagoRef.current && Date.now() - enPagoDesdeRef.current > 10 * 60 * 1000;
    if (enPagoRef.current && !pagoTrabado()) return;
    consultandoRef.current = true;
    try {
      const { data } = await axios.get(`${apiUrl}/api/pos/sessions/check`, { params: { session_id: sessionId }, timeout: 6000 });
      if (data && data.success === true) odooOkRef.current = Date.now();   // Odoo respondió de verdad (hay internet)
      const cerrada = !!data && (data.is_open === false || ['closed', 'closing_control', 'not_found'].includes(data.state));
      if (cerrada && (!enPagoRef.current || pagoTrabado())) setCajaCerrada(true);
    } catch (e) { /* sin conexión: no se bloquea */ }
    finally { consultandoRef.current = false; ultimaConsultaRef.current = Date.now(); }
  }, [apiUrl, sessionId]);

  useEffect(() => {
    if (!isAuthenticated || !sessionId) return undefined;
    let activo = true; let timer = null; let gen = 0;
    const ciclo = async (g) => {
      await verificarSesion();
      if (activo && g === gen) timer = setTimeout(() => ciclo(g), 5000);
    };
    ciclo(gen);
    // Vigilante: si el ciclo se perdió o se colgó (más de 20 s sin consultar), lo relanza.
    const vigilante = setInterval(() => {
      if (Date.now() - ultimaConsultaRef.current > 20000) { clearTimeout(timer); consultandoRef.current = false; gen += 1; ciclo(gen); }
    }, 5000);
    const alVolver = () => { if (document.visibilityState === 'visible') verificarSesion(); };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    return () => {
      activo = false; clearTimeout(timer); clearInterval(vigilante);
      document.removeEventListener('visibilitychange', alVolver); window.removeEventListener('focus', alVolver);
    };
  }, [isAuthenticated, sessionId, verificarSesion]);

  // Al cambiar de pantalla se consulta de inmediato (antes de pagar, al volver de pago, al empezar un pedido).
  useEffect(() => {
    if (isAuthenticated && sessionId) verificarSesion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Recarga diaria a las 5:00 si la página se cargó antes de esa hora y el totem está libre (sin pedido ni pago):
  // red de seguridad contra cualquier estado raro. Al recargar pide el PIN de nuevo (toma la sesión del día).
  // Solo con internet: la página viene del servidor, y recargarla sin conexión dejaría el totem en la pantalla
  // de error del navegador. Se exige que Odoo haya respondido hace poco y que la página se pueda descargar;
  // si no, se vuelve a intentar a los 30 s (sin internet todo el día, simplemente no recarga).
  useEffect(() => {
    let probando = false;
    const t = setInterval(async () => {
      const ahora = new Date();
      const cinco = new Date(ahora); cinco.setHours(5, 0, 0, 0);
      const libre = !enPagoRef.current && itemsRef.current === 0;
      if (!(ahora >= cinco && cargadoEnRef.current < cinco.getTime() && libre) || probando) return;
      if (!navigator.onLine || Date.now() - odooOkRef.current > 60000) return;
      probando = true;
      try {
        const r = await fetch(`${window.location.origin}/index.html?_=${Date.now()}`, { cache: 'no-store' });
        if (r.ok && !enPagoRef.current && itemsRef.current === 0) window.location.reload();
      } catch (e) { /* sin conexión al servidor de la página: no se recarga */ }
      finally { probando = false; }
    }, 30000);
    return () => clearInterval(t);
  }, []);

  const { isInactive, resetTimer, stopTimer, startTimer } = useInactivityTimer(60000);

  const shouldProcessInactivity = isAuthenticated &&
    isInactive &&
    !showWelcome &&
    location.pathname !== '/login' &&
    location.pathname !== '/payment' &&
    location.pathname !== '/payment-method' &&
    location.pathname !== '/order-type' &&
    location.pathname !== '/operate-card' &&
    location.pathname !== '/thank-you';

  useEffect(() => {
    if (isAuthenticated && location.pathname !== '/login') {
      if (location.pathname === '/payment' ||
        location.pathname === '/payment-method' ||
        location.pathname === '/order-type' ||
        location.pathname === '/operate-card' ||
        location.pathname === '/thank-you') {
        stopTimer();
      } else {
        startTimer();
      }
    } else {
      stopTimer();
    }
  }, [isAuthenticated, location.pathname, startTimer, stopTimer]);

  useEffect(() => {
    if (shouldProcessInactivity && !showTimeoutWarning) {
      if (items.length === 0) {
        setShowWelcome(true);
      } else {
        setShowTimeoutWarning(true);
      }
    }
  }, [shouldProcessInactivity, items.length, showTimeoutWarning]);

  const handleLogin = () => {
    setIsAuthenticated(true);
    resetTimer();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    clearSessionData();
    setShowWelcome(false);
    stopTimer();
  };

  const handleWelcomeStart = () => {
    setShowWelcome(false);
    resetTimer();
    navigate('/products'); // directo al menú; el tipo de pedido se pregunta antes de pagar
  };

  const handleContinue = useCallback(() => {
    setShowTimeoutWarning(false);
    resetTimer();
  }, [resetTimer]);

  const handleExit = useCallback(() => {
    clearCart();
    setShowTimeoutWarning(false);
    setShowWelcome(true);
    resetTimer();
  }, [clearCart, resetTimer]);

  // Sesión cerrada: se vuelve solo al inicio de sesión a los pocos segundos (o antes, tocando el botón).
  // Antes se borra el caché de sesiones del ApiProxy y la sesión guardada en el totem: si no, el login podría
  // devolver la sesión vieja y el cartel reaparecería aunque ya hayan abierto una nueva.
  const volverAlInicio = async () => {
    try { await axios.post(`${apiUrl}/api/pos/sessions/clear-cache`, {}, { timeout: 5000 }); } catch (e) { /* sin ApiProxy: se sigue igual */ }
    try { OfflineService.clearSessionInfo(); } catch (e) { /* nada guardado */ }
    clearCart();
    setCajaCerrada(false);
    handleLogout();
    navigate('/login');
  };
  useEffect(() => {
    if (!cajaCerrada) return undefined;
    const t = setTimeout(() => { volverAlInicio(); }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cajaCerrada]);

  if (cajaCerrada) {
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: '#1A1A1A', zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ backgroundColor: '#ffffff', padding: '3rem', borderRadius: '1.5rem', textAlign: 'center', maxWidth: '34rem', width: '90%' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#111827', margin: '0 0 1rem 0' }}>Caja cerrada</h2>
          <p style={{ fontSize: '1.5rem', color: '#4B5563', margin: '0 0 2rem 0' }}>
            Este totem no puede recibir pedidos por ahora. Volviendo al inicio…
          </p>
          <button onClick={volverAlInicio} style={{ padding: '1.25rem 2.5rem', backgroundColor: '#DA291C', color: 'white', fontWeight: 'bold', borderRadius: '1rem', fontSize: '1.5rem', border: 'none', cursor: 'pointer' }}>
            Ir al inicio ahora
          </button>
        </div>
      </div>
    );
  }

  if (showWelcome) {
    return <WelcomeScreen onStart={handleWelcomeStart} />;
  }

  return (
    <>
      <Routes>
        <Route path="/login" element={<TokenLogin onLogin={handleLogin} />} />
        <Route
          path="/"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <ProductListWithSidebar />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/products"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <ProductListWithSidebar />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cart"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <CartScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <CategorySelectionScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/products/:categoryId"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <ProductList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <PaymentScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/order-type"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <OrderTypeScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-method"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <PaymentMethodScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/operate-card"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <OperateCardScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/thank-you"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <ThankYouScreen />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/preview-thank-you"
          element={<ThankYouScreen previewNumber="K42" />}
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      {showTimeoutWarning && (
        <TimeoutWarningModal onContinue={handleContinue} onExit={handleExit} />
      )}
    </>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    connectionService.forceCheck().catch(() => {
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <TbkConnectionProvider>
          <XSignConnectionProvider>
            <CartProvider>
              <Router>
                <AppRoutes
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                />
                <KioskExitGate />
              </Router>
            </CartProvider>
          </XSignConnectionProvider>
        </TbkConnectionProvider>
      </AppProvider>
    </QueryClientProvider>
  );
}

export default App;